// @flow

import { useCallback } from 'react';

import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { selectCurrency as selectPrepaidCurrency } from '@gandi/billing';
import { useResourcePost, useResourcePatch } from '@gandi/resource/react';
import { useTranslator } from '@gandi/translate';

import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { organizationResource } from 'src/modules/organization/resources';

import { createCustomer, editCustomer } from '../../Customer/modules/customers';
import { setPatchContactsFlag } from '../../DomainContacts/Wizard/modules/actions';

import {
  getEnrichedDataForPOST,
  getFilteredDataForPATCH,
} from '../modules/helpers';

import type { Contact, OrganizationFormData, Role } from '../types';

import { useHandleFormErrorSubmit } from './useHandleFormError';
import { useHandleFormSuccess } from './useHandleFormSuccess';

export const useOnSubmit = ({
  isEditingMode,
  dirtyFields,
  setDisabled,
  contact,
  form,
  firstBuy,
  chooseProductOwner,
  role,
  setCurrentRadioValue,
  onSuccess,
  delegate,
  isCustomer = false,
  studio,
}: {
  chooseProductOwner: boolean,
  isEditingMode: boolean,
  setDisabled: React.ReactSetStateFunction<boolean>,
  studio?: boolean,

  // Main props
  contact?: Contact,
  firstBuy?: boolean,
  onSuccess?: () => void,
  role?: Role,
  setCurrentRadioValue?: React.ReactSetStateFunction<?string>,
  delegate: 'yes' | 'no',
  isCustomer?: boolean,

  // get this from the FormContext?
  dirtyFields: any,
  form: any,
}): any => {
  const { __ } = useTranslator();

  const dispatch = useDispatch();

  const params = useParams<{ cartId: string }>();

  const { setSubmitting, cartOwnerOrganization } = useCartSummaryContext();

  const { handlePatch } = useResourcePatch(organizationResource, {
    id: contact?.id,
  });

  const { handlePost } = useResourcePost(organizationResource);

  const currencyState = useAppSelector(state =>
    contact?.id
      ? selectPrepaidCurrency(state, { sharingId: contact?.id })
      : undefined,
  );

  const currency = form.watch('currency');
  const type = form.watch('type');

  const onStartSubmitting = useCallback(() => {
    // Needed for: updateProductsPayload.
    // $FlowFixMe[not-an-array]
    dispatch(setPatchContactsFlag(true));
    setSubmitting(true);
    setDisabled(true);
  }, [dispatch, setDisabled, setSubmitting]);

  const onStopSubmitting = useCallback(() => {
    setSubmitting(false);
    setDisabled(false);
  }, [setDisabled, setSubmitting]);

  const formSuccessCallback = useHandleFormSuccess({
    firstBuy,
    chooseProductOwner,
    type,
    role,
    currency,
    currencyState,
    contact,
    setCurrentRadioValue,
    onSuccess,
    studio,
  });

  const formErrorCallback = useHandleFormErrorSubmit(form);

  const patchCallback = useCallback(
    (data: OrganizationFormData) => {
      const filteredData = getFilteredDataForPATCH({
        data,
        dirtyFields,
      });

      // PATCH only if there is something to patch
      if (filteredData && Object.keys(filteredData).length > 0) {
        onStartSubmitting();
        // $FlowFixMe[prop-missing] type of data is not well set
        handlePatch(filteredData)
          .then(formSuccessCallback)
          .catch(formErrorCallback)
          .finally(onStopSubmitting);
      }
    },
    [
      dirtyFields,
      formErrorCallback,
      formSuccessCallback,
      handlePatch,
      onStartSubmitting,
      onStopSubmitting,
    ],
  );

  const postCallback = useCallback(
    ({ msa, ...data }: OrganizationFormData) => {
      onStartSubmitting();

      const newData =
        data.type === 'individual' || delegate === 'yes'
          ? {
              ...data,
              ...getEnrichedDataForPOST({
                ownerOrganization: cartOwnerOrganization,
                cartId: params.cartId,
                label: __('Admin'),
              }),
            }
          : data;

      // $FlowFixMe[prop-missing] type of data is not well set
      // $FlowFixMe[incompatible-call] type of data is not well set
      handlePost(newData)
        .then(formSuccessCallback)
        .catch(formErrorCallback)
        .finally(onStopSubmitting);
    },
    [
      __,
      delegate,
      formErrorCallback,
      formSuccessCallback,
      handlePost,
      onStartSubmitting,
      onStopSubmitting,
      cartOwnerOrganization,
      params.cartId,
    ],
  );

  const resellerId = useAppSelector(state => state.cart.owner);

  const patchCustomerCallback = useCallback(
    (data: OrganizationFormData) => {
      // some check for flow
      if (!resellerId || !contact) return;

      const filteredData = getFilteredDataForPATCH({
        data,
        dirtyFields,
      });

      // PATCH only if there is something to patch
      if (filteredData && Object.keys(filteredData).length > 0) {
        onStartSubmitting();
        // $FlowFixMe[prop-missing] OrganizationFormData missing:  biller_id, corporate, id, parent, reference
        // $FlowFixMe[prop-missing] CustomerFormData missing: currency, name
        // $FlowFixMe[incompatible-call] some fields are "required" but we do not went to sent them anyway
        dispatch(editCustomer(resellerId, contact.id, filteredData))
          .then(formSuccessCallback)
          .catch(formErrorCallback)
          .finally(onStopSubmitting);
      }
    },
    [
      dirtyFields,
      dispatch,
      formErrorCallback,
      formSuccessCallback,
      onStartSubmitting,
      onStopSubmitting,
      contact,
      resellerId,
    ],
  );

  const postCustomerCallback = useCallback(
    (data: OrganizationFormData) => {
      // some check for flow
      if (!resellerId) return;

      onStartSubmitting();

      // $FlowFixMe[incompatible-call] createCustomer is asking for another argument which should be `options`. However it an empty object by default.
      // $FlowFixMe[incompatible-call] zip is set as string from the form but CustomerFormData want a number :(
      // $FlowFixMe[prop-missing] biller_id, corporate, id, parent, reference are missing from OrganizationFormData but required for editCustomer.
      // $FlowFixMe[prop-missing] currency, name are missing from CustomerFormData.
      dispatch(createCustomer(resellerId, data))
        .then(formSuccessCallback)
        .catch(formErrorCallback)
        .finally(onStopSubmitting);
    },
    [
      dispatch,
      formErrorCallback,
      formSuccessCallback,
      onStartSubmitting,
      onStopSubmitting,
      resellerId,
    ],
  );

  return isCustomer
    ? isEditingMode
      ? patchCustomerCallback
      : postCustomerCallback
    : isEditingMode
      ? patchCallback
      : postCallback;
};
